<template>
    <DashboardLayout>
        <div class="d-flex justify-content-center">
            <div class="form-outer">
                <div class="crud-form position-relative mb-3">
                    <div v-if="userStore.profile">
                        <div class="form-inner mb-2">
                            <div class="form-body">
                                <!-- Profile Header -->
                                <div class="profile-header">
                                    <div class="profile-photo-wrapper">
                                        <img :src="userStore.avatar" alt="User Photo" class="profile-photo">
                                        <div v-if="isLoading" class="photo-overlay">
                                            <span class="spinner-border spinner-border-sm"></span>
                                        </div>
                                    </div>

                                    <div class="profile-actions">
                                        <input type="file" accept="image/*" ref="photoInput" @change="uploadPhoto"
                                            hidden />
                                        <button class="upload-btn" :disabled="isLoading" @click="triggerPhotoInput">
                                            <span class="material-symbols-outlined">edit</span>
                                            {{ isLoading ? 'Uploading...' : 'Update Photo' }}
                                        </button>
                                        <button v-if="userStore.hasRealPhoto" class="delete-btn btn btn-sm btn-danger"
                                            :disabled="isLoading" @click="deletePhoto">
                                            <span class="material-symbols-outlined">delete</span>
                                        </button>
                                    </div>
                                </div>

                                <!-- Info Section -->
                                <div class="info-section">
                                    <div class="info-item">
                                        <span>Username</span>
                                        <p>{{ userStore.profile.userName }}</p>
                                    </div>

                                    <div class="info-item">
                                        <span>First Name</span>
                                        <p>{{ userStore.profile.firstName }}</p>
                                    </div>

                                    <div class="info-item">
                                        <span>Last Name</span>
                                        <p>{{ userStore.profile.lastName }}</p>
                                    </div>

                                    <div class="info-item">
                                        <span>Role</span>
                                        <p>{{ userStore.profile.roleName }}</p>
                                    </div>

                                    <div class="info-item">
                                        <span>Email</span>
                                        <p>{{ userStore.profile.email }}</p>
                                    </div>

                                    <div class="info-item">
                                        <span>Appraisal Remark</span>
                                        <div v-if="userStore.profile.appraisalRemark || !editingRemark"
                                            class="remark-content">
                                            <p>{{ userStore.profile.appraisalRemark ?? 'No remark provided' }}
                                            </p><span class="material-symbols-outlined edit-icon"
                                                title="Edit Appraisal Remark" @click="toggleEditField()">
                                                edit
                                            </span>
                                        </div>

                                        <div v-if="editingRemark" class="remark-content">
                                            <input type="text" class="form-control" v-model="form.appraisalRemark"
                                                style="width:350px" placeholder="Enter your default appraisal remark">
                                            <button class="save-btn" title="Save Appraisal Remark"
                                                :disabled="isLoadingRemark"
                                                @click="updateAppraisalRemark(form.appraisalRemark)">
                                                {{ isLoadingRemark ? 'Saving Remark...' : 'Save' }}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Signature Section -->
                                <div v-if="hasAccess(['groupHead', 'dirOfBudget', 'perSec', 'commOfBAndP', 'dirCAD', 'dirOfPayment', 'dirOfTreasury', 'acctGeneral'])"
                                    class="signature-section">
                                    <div class="info-section">
                                        <!-- Signature -->
                                        <div class="info-item">
                                            <div class="signature-header">
                                                <span>E-signature</span>
                                                <span class="material-symbols-outlined info-icon"
                                                    title="Signature must be in black ink and plain white background">
                                                    info
                                                </span>
                                            </div>

                                            <div class="signature-content">
                                                <template v-if="userStore.signature">
                                                    <img :src="userStore.signature" class="profile-signature"
                                                        @click="showSignatureModal = true">
                                                </template>

                                                <template v-else>
                                                    <p class="no-signature-text">No signature yet</p>
                                                </template>

                                                <input type="file" accept="image/*" ref="signatureInput"
                                                    @change="uploadSignature" hidden />

                                                <!-- Modal -->
                                                <div v-if="showSignatureModal" class="signature-modal"
                                                    @click="showSignatureModal = false">
                                                    <img :src="userStore.signature" class="signature-expanded">
                                                </div>

                                                <button class="upload-btn sign-btn" :disabled="isLoadingSign"
                                                    @click="triggerSignatureInput">
                                                    <span class="material-symbols-outlined"
                                                        style="color: white !important">edit</span>
                                                    {{ isLoadingSign ? 'Uploading...' : 'Upload Signature' }}
                                                </button>
                                            </div>
                                        </div>

                                        <!-- Example Signature-->
                                        <div class="info-item">
                                            <div class="signature-header">
                                                <span>Example Signature</span>
                                                <span class="material-symbols-outlined info-icon"
                                                    title="Example signature for reference. Click to enlarge.">
                                                    info
                                                </span>
                                            </div>

                                            <div class="signature-content">
                                                <img src="@/assets/images/example-sign.jpeg" class="profile-signature"
                                                    @click="showExampleModal = true">
                                            </div>

                                            <!-- Modal -->
                                            <div v-if="showExampleModal" class="signature-modal"
                                                @click="showExampleModal = false">
                                                <img src="@/assets/images/example-sign.jpeg" class="signature-expanded">
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                <div class="crud-form position-relative">
                    <div class="form-body">
                        <!-- Color Themes -->
                        <div class="form-island-header">
                            <h5 class="fw-semibold">Color Themes</h5>
                        </div>
                        <div class="form-island-body">
                            <p>Choose your preferred color theme.</p>
                            <div class="d-flex gap-3 mt-2">
                                <!-- Light Theme -->
                                <div class="theme">
                                    <button class="btn btn-outline-secondary color" 
                                    :class="{ selected: theme === 'light' }" @click="setTheme('light')">
                                        <span class="material-symbols-outlined">
                                            light_mode
                                        </span>
                                    </button>
                                    <p class="mt-2">Light Theme</p>
                                </div>
                                <!-- Dark Theme -->
                                <div class="theme">
                                    <button class="btn btn-outline-dark color" 
                                    :class="{ selected: theme === 'dark' }" @click="setTheme('dark')">
                                        <span class="material-symbols-outlined">
                                            dark_mode
                                        </span>
                                    </button>
                                    <p class="mt-2">Dark Theme</p>
                                </div>
                                <!-- Default theme -->
                                <div class="theme">
                                    <button class="btn btn-outline-default color default-color"
                                    :class="{ selected: theme === 'default' }" @click="setTheme('default')">
                                    </button>
                                    <p class="mt-2">Default Theme</p>
                                </div>
                                <!-- Color theme 1 -->
                                <div class="theme">
                                    <button class="btn btn-outline-default color color-theme-1"
                                    :class="{ selected: theme === 'theme-1' }" @click="setTheme('theme-1')">
                                    </button>
                                    <p class="mt-2">Blue Theme</p>
                                </div>
                                <!-- Color theme 2 -->
                                <div class="theme">
                                    <button class="btn btn-outline-default color color-theme-2"
                                    :class="{ selected: theme === 'theme-2' }" @click="setTheme('theme-2')">
                                    </button>
                                    <p class="mt-2">Teal Theme</p>
                                </div>
                                <!-- Color theme 3 -->
                                <div class="theme">
                                    <button class="btn btn-outline-default color color-theme-3"
                                    :class="{ selected: theme === 'theme-3' }" @click="setTheme('theme-3')">
                                    </button>
                                    <p class="mt-2">Bronze Theme</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import { ref, defineOptions, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import '@/assets/css/dashboard.css'
import axios from 'axios'
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import DashboardLayout from './layout/DashboardLayout.vue'
import blankAvatar from '@/assets/images/blank-profile-avatar.png'
import { useTheme } from '@/composables/useTheme'
const { theme, setTheme } = useTheme()

const userStore = useUserStore()
const isLoading = ref(false);
const isLoadingRemark = ref(false);
const isLoadingSign = ref(false);

const photoInput = ref(null)
const signatureInput = ref(null)

const triggerPhotoInput = () => photoInput.value?.click()
const triggerSignatureInput = () => signatureInput.value?.click()

const showSignatureModal = ref(false)
const showExampleModal = ref(false)

const editingRemark = ref(false)
const root_api_url = process.env.VUE_APP_API_ROOT_URL

const form = ref({
    appraisalRemark: userStore.profile?.appraisalRemark || '',
})

defineOptions({
    name: 'ProfilePage',
})

const allRoles = [
    'qssu',
    'dirOfBudget',
    'perSec',
    'commOfBAndP',
    'registryOfficer',
    'groupHead',
    'groupMember',
    'dirCAD',
    'dirOfPayment',
    'dirOfTreasury',
    'acctGeneral',
];

const hasAccess = (required) => {
    const profileRoles = userStore.profile?.role || {}; // object of roles

    // Helper to check if user has a role
    const userHasRole = (role) => !!profileRoles[role];

    if (!required || required.length === 0) return true; // no restriction

    // If array contains empty string
    if (required.includes('')) {
        const otherRoles = required.filter(role => role !== '');

        // Explicitly listed roles are allowed
        if (otherRoles.some(role => userHasRole(role))) return true;

        // Free users (roles not in allRoles) get full access
        const isFreeUser = Object.keys(profileRoles).every(r => !allRoles.includes(r) || profileRoles[r] === false);
        if (isFreeUser) return true;

        // Predefined roles not explicitly listed are denied
        return false;
    }

    // Normal role check
    return required.some(role => userHasRole(role));
}

function toggleEditField() {
    editingRemark.value = !editingRemark.value;
}

const updateAppraisalRemark = async (newRemark) => {
    if (newRemark.trim() === (userStore.profile.appraisalRemark?.trim())) {
        toggleEditField();
        return; // No change, just toggle back
    }

    if (newRemark.length > 255) {
        await Swal.fire({
            icon: 'warning',
            title: 'Remark too long',
            text: 'Appraisal remark must be 255 characters or less.',
        })
        return;
    }

    if (newRemark.trim() === '' || null) {
        await Swal.fire({
            icon: 'warning',
            title: 'Remark cannot be empty',
            text: 'Appraisal remark must contain characters.',
        })
        return;
    }

    isLoadingRemark.value = true;
    const token = Cookies.get('auth_token')
    const appraisalRemark = newRemark.trim(); // Send null if empty to clear remark
    try {
        const response = await axios.post(`${root_api_url}/auth/user/remark/${appraisalRemark}`, appraisalRemark, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
            }
        })
        userStore.profile.appraisalRemark = response.data; // Update local state with new remark
        await Swal.fire({
            icon: 'success',
            title: 'Saved!',
            text: 'Your appraisal remark has been updated.',
            timer: 1500,
            showConfirmButton: false
        });
    } catch (error) {
        console.error('Failed to update appraisal remark:', error);
        await Swal.fire({
            icon: 'error',
            title: 'Failed',
            text: 'Could not save appraisal remark. Try again.',
        });
    } finally {
        isLoadingRemark.value = false;
        toggleEditField();
    }
}

const uploadPhoto = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif']
    const maxSize = 1 * 1024 * 1024 // 1MB

    if (!allowedTypes.includes(file.type)) {
        await Swal.fire({
            icon: 'warning',
            title: 'Invalid file type selected',
            text: 'Only JPG, PNG, or GIF images are allowed.',
        })
        return
    }

    if (file.size > maxSize) {
        await Swal.fire({
            icon: 'warning',
            title: 'File too large',
            text: 'Size must be less than 1MB.',
        })
        return
    }

    const token = Cookies.get('auth_token')
    const formData = new FormData()
    formData.append('image', file)

    isLoading.value = true

    try {
        const response = await axios.post(`${root_api_url}/auth/user/photo`, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
            }
        })
        const photo = response.data;

        userStore.profilePhoto = photo.imageContent
            ? `data:image/png;base64,${photo.imageContent}`
            : blankAvatar;
        // console.log('Upload successful:', response.data)
    } catch (error) {
        console.error('Upload failed:', error)
    } finally {
        event.target.value = null
        isLoading.value = false
    }
}

const deletePhoto = async () => {
    const confirm = await Swal.fire({
        title: 'Delete Photo?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it',
    })

    if (!confirm.isConfirmed) return

    const token = Cookies.get('auth_token')

    isLoading.value = true

    try {
        const response = await fetch(blankAvatar)
        const blob = await response.blob()
        const formData = new FormData()
        formData.append('image', blob, blankAvatar) // you'd need to convert it

        await axios.post(`${root_api_url}/auth/user/photo`, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
            }
        })

        // ✅ Update UI AFTER backend success
        userStore.profilePhoto = blankAvatar

        await Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Profile photo removed successfully.',
            timer: 1500,
            showConfirmButton: false
        })

        console.log("avatar:", userStore.avatar)
    } catch (error) {
        console.error('Delete failed:', error)

        await Swal.fire({
            icon: 'error',
            title: 'Failed',
            text: 'Could not delete photo. Try again.',
        })
    } finally {
        isLoading.value = false
    }
}

const uploadSignature = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg']
    const maxSize = 1 * 1024 * 1024

    if (!allowedTypes.includes(file.type)) {
        await Swal.fire({
            icon: 'warning',
            title: 'Invalid file type selected',
            text: 'Only JPG, PNG, or GIF images are allowed.',
        })
        return
    }

    if (file.size > maxSize) {
        await Swal.fire({
            icon: 'warning',
            title: 'File too large',
            text: 'Signature must be less than 1MB.',
        })
        return
    }

    const token = Cookies.get('auth_token')
    const formData = new FormData()
    formData.append('image', file)

    isLoadingSign.value = true

    try {
        const response = await axios.post(`${root_api_url}/auth/user/sign`, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
            }
        })
        const signature = response.data;

        userStore.profileSignature = signature.imageContent
            ? `data:image/png;base64,${signature.imageContent}`
            : null;

        // console.log('Upload successful:', response.data)
    } catch (error) {
        console.error('Upload failed:', error)
    } finally {
        event.target.value = null
        isLoadingSign.value = false
    }
}

onMounted(() => {
    document.title = "Profile"
});
</script>

<style scoped>
.profile-photo-wrapper {
    position: relative;
}

.photo-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    height: 120px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.profile-photo {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

.profile-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.profile-signature {
    width: 200px;
    /* height: 100px; */
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

.profile-signature img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.no-signature-text {
    font-style: italic;
    color: #6c757d;
    /* muted gray */
    margin-top: 10px;
}

.signature-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
}

.signature-preview {
    max-height: 80px;
    border: 1px solid var(--border);
    padding: 5px;
    background: #fff;
}

.signature-expanded {
    max-width: 90%;
    max-height: 90%;
    border-radius: 8px;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.6);
}

.sign-btn {
    height: 40px !important;
}

.upload-btn {
    background-color: var(--btn-primary) !important;
    margin: 10px;
    height: 50px;
    font-weight: 600;
    color: white !important;
    border: none !important;
    border-radius: 0px !important;
    padding: 10px;
}

.upload-btn span {
    margin-right: 5px;
    font-weight: 600;
}

.delete-btn {
    height: 50px;
    border-radius: 6px !important;
    display: flex;
    align-items: center;
    color: white !important;
    border: none !important;
    padding: 12px;
    transition: background-color 0.3s ease;
}

.material-symbols-outlined {
    font-size: 1.25rem;
}

.edit-icon {
    font-size: 16px;
    color: #aaa;
    cursor: pointer;
}

.edit-icon-filled {
    font-variation-settings:
        'FILL' 1,
        'wght' 400,
        'GRAD' 0,
        'opsz' 24
}

.form-outer {
    background: 0;
}

/* CARD CLEANUP */
.crud-form {
    background: #fff;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}

/* PROFILE HEADER */
.profile-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 2rem;
}

.profile-photo {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 1rem;
    border: 3px solid #f1f1f1;
}

.profile-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

/* INFO SECTION */
.info-section {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.2rem 2rem;
    margin-bottom: 2rem;
}

.info-item span {
    font-size: 0.85rem;
    color: #888;
}

.info-item p {
    margin: 0;
    font-weight: 500;
    font-size: 0.95rem;
}

/* SIGNATURE */
.signature-section {
    border-top: 1px solid #eee;
    padding-top: 1.5rem;
}

.signature-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    margin-bottom: 1rem;
}

.signature-content {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
}

.profile-signature {
    max-width: 180px;
    cursor: zoom-in;
}

/* BUTTON IMPROVEMENT */
.upload-btn {
    background-color: var(--btn-primary) !important;
    color: white !important;
    border-radius: 6px !important;
    padding: 8px 12px;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    gap: 5px;
    transition: 0.2s ease;
}

.upload-btn:hover {
    background-color: var(--btn-primary-hover) !important;
    transform: translateY(-1px);
}


.save-btn {
    height: auto;
    font-weight: 600;
    border: none !important;
    background-color: #339900 !important;
    color: white !important;
    border-radius: 6px !important;
    padding: 8px 12px;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    gap: 5px;
    transition: 0.2s ease;
}

.save-btn:hover {
    background-color: #267a00 !important;
    transform: translateY(-1px);
}

/* TEXT */
.no-signature-text {
    font-style: italic;
    color: #999;
}

/* ICON */
.info-icon {
    font-size: 16px;
    color: #aaa;
    cursor: help;
}

.remark-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.theme {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 5px;
    width: 80px;
}

.color {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 100%;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.color:hover {
transform: scale(1.15) rotate(180deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.color:active {
    transform: scale(1.05) rotate(180deg);
}

.color.selected {
    outline: 2px solid var(--btn-primary);
    outline-offset: 3px;
}

.default-color {
    background-image: linear-gradient(135deg, #f2ffe6 0 33.33%,#e6ffcc 33.33% 66.66%, #b3ff66 66.66% 100%) !important;
    border: 1px solid #212529 !important;
}

/* blue */
.color-theme-1 {
  background-image: linear-gradient(135deg, #eaf0f7 0 33.33%, #dce7f3 33.33% 66.66%, #c9daee 66.66% 100%) !important;
  border: 1px solid #212529 !important;
}

/* teal */
.color-theme-2 {
  background-image: linear-gradient(135deg, #e4f1f3 0 33.33%, #d3e8ec 33.33% 66.66%, #b8dbe1 66.66% 100%) !important;
  border: 1px solid #212529 !important;
}

/* bronze */
.color-theme-3 {
  background-image: linear-gradient(135deg, #f5f0e4 0 33.33%, #ece3cb 33.33% 66.66%, #e0d2ae 66.66% 100%) !important;
  border: 1px solid #212529 !important;
}


</style>
